import {
    ICorporationResponse,
    ICurrentUserResponse,
} from '@kcftech/prometheustypescriptapiclient';
import TermsOfUse from 'assets/TermsOfUse.pdf';
import { Auth } from 'auth-provider';
import { Button } from 'components/button/Button';
import { useAccess } from 'contexts/AccessContext';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { sdClientMetadata } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { getKCFAcademyUrl } from 'pages/Location/shared';
import {
    browserName,
    deviceType,
    fullBrowserVersion,
    osName,
    osVersion,
} from 'react-device-detect';
import { ExternalLink } from 'routing/ExternalLink';
import styled from 'styled-components';
import { link as linkColor } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';
import { AppInfo } from 'utils/AppInfo';
import { copyToClipboard } from 'utils/clipboard';

const MenuButton = styled(Button)`
    color: ${linkColor};
    &:hover,
    &:focus {
        background-color: ${KCFColor.primaryNavigationHover};
    }
    &:active {
        background-color: ${KCFColor.secondaryNavigationBackground};
    }
`;

const copySupportInfoToClipboard = async (
    user: ICurrentUserResponse | undefined,
    corporation: ICorporationResponse | undefined,
    callback: { onSuccess: () => void; onError: () => void }
) => {
    const supportInfo = [
        `URL: ${window.location.href}`,
        `Version: ${
            AppInfo.release
                ? `${AppInfo.version}+${AppInfo.build}`
                : `${AppInfo.version}-prerelease`
        }`,
        `API Version: ${sdClientMetadata.PrometheusApiVersion ?? 'unknown'}`,
        `Browser: ${browserName} ${fullBrowserVersion}`,
        `OS: ${osName} ${osVersion}`,
        `Device Type: ${deviceType}`,
        `Resolution: ${window.innerWidth}x${window.innerHeight} [DPI: ${window.devicePixelRatio}]`,
        `User: ${user?.FirstName} ${user?.LastName} <${user?.Email}> [Role: ${user?.ApplicationRole}]`,
        `Language: ${navigator.language}`,
        'User Preferences:',
        `\tDate Format: ${user?.Profile.DateFormat}`,
        `\tTime Format: ${user?.Profile.TimeFormat}`,
        `\tTime Zone: ${user?.Profile.TimeZones}`,
        `\tAcceleration Units: ${user?.Profile.AccelerationUnits}`,
        `\tVelocity Units: ${user?.Profile.VelocityUnits}`,
        `\tTemperature Units: ${user?.Profile.TemperatureUnits}`,
        `\tPressure Units: ${user?.Profile.PressureUnits}`,
        corporation !== undefined
            ? `Access Level for current Corporation: ${corporation.AccessLevel.toString()}`
            : undefined,
    ].join('\n');

    try {
        await copyToClipboard(supportInfo);
        callback.onSuccess();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        callback.onError();
    }
};

export function HelpMenu(): JSX.Element {
    const { corporation } = useRouteEntities();
    const user = useUser();

    const { showToast } = useNotificationContext();

    const onCopyAuthClick = async () => {
        try {
            const session = await Auth.currentSession();
            const jwt = session.getAccessToken().getJwtToken();
            copyToClipboard(jwt);
            showToast({ type: 'success', content: 'Auth token copied' });
        } catch {
            showToast({ type: 'error', content: 'Failed to copy auth token' });
        }
    };

    const { isPremium } = useAccess();

    const version = `${AppInfo.displayVersion()} (API: ${
        sdClientMetadata.PrometheusApiVersion ?? 'unknown'
    })`;

    return (
        <div className="flex-column align-start m-20 fine-print">
            <span className="flex-row full-width mb-5">
                <span data-cy="help-menu-version-number">Version: </span>
                <MenuButton
                    data-cy="help-menu-build-number"
                    title="Copy build & debug information to clipboard"
                    onClick={() => {
                        copySupportInfoToClipboard(user.user, corporation, {
                            onSuccess: () =>
                                showToast({
                                    type: 'success',
                                    content: 'Debug information copied to clipboard.',
                                }),
                            onError: () =>
                                showToast({
                                    type: 'error',
                                    content: 'Error copying debug information.',
                                }),
                        });
                    }}
                >
                    {version}
                </MenuButton>
            </span>
            <span className="flex-row full-width mb-5">
                {corporation !== undefined && (
                    <span>Access Level: {isPremium ? 'Premium' : 'Basic'}</span>
                )}
            </span>
            <div className="flex-row mb-10">
                <ExternalLink
                    data-cy="help-menu-status-link"
                    url="https://kcfstatus.com"
                    className="mr-10"
                >
                    Status Page
                </ExternalLink>
                |
                <ExternalLink
                    data-cy="help-menu-support-link"
                    url="https://support.kcftech.com/hc/en-us?flash_digest=23f6328d79cd99170570c79cde80ddf8df0add9c"
                    className="mx-10"
                >
                    Help Center
                </ExternalLink>
                |
                <ExternalLink
                    data-cy="kcf-academy-link"
                    url={getKCFAcademyUrl()}
                    className="ml-10"
                >
                    KCF Academy
                </ExternalLink>
            </div>
            <span aria-label="Copyright">
                Copyright &copy;
                <ExternalLink
                    data-cy="help-menu-website-link"
                    url="https://kcftech.com/"
                    className="mr-5 ml-5"
                >
                    KCF Technologies, Inc.
                </ExternalLink>
                <span>{new Date().getFullYear()}</span>
            </span>
            <span className="flex-row full-width">
                <span className="mr-10">All Rights Reserved</span>|
                <ExternalLink
                    data-cy="help-menu-terms-of-use-link"
                    url={TermsOfUse}
                    className="ml-10"
                >
                    Terms of Use
                </ExternalLink>
            </span>
            <div className="pt-10">
                <MenuButton className="px-0" onClick={onCopyAuthClick}>
                    Copy Auth Token
                </MenuButton>
            </div>
        </div>
    );
}
